<script setup lang="ts">
import { useExtendedI18n } from '@/i18n'
import { computed, ref, watch } from 'vue'
import { VButton, VIcon, VTextField } from '@/modules/shared/components'
import { format } from '@/modules/shared/utils/v-table'

const { n } = useExtendedI18n()
const props = defineProps<{
  disbursement_transfers: any[]
  entity: any
  is_disbursement_new: boolean
  aggregateValues: any
  disbursement_type: 'cash' | 'asset'
  type: 'interest' | 'normal'
  toggleAllInvestor: (checked: boolean) => void
  toggleInvestor: (transfer: any, is_active: boolean) => void
  onChangeTransfer: (transfer: any, key: string) => void
  getCarriedInterest: (transfer: any) => number
}>()

const disbursement_transfers = computed(() => props.disbursement_transfers)
const aggregateValues = computed(() => props.aggregateValues)
const is_interest = computed(() => props.type === 'interest')

const columns = ref([
  { key: 'total_capital', label: 'Gross Distributed', visible: true, color: '!bg-[#408F93]/10' },
  {
    key: 'capital',
    label: 'Return of Capital',
    visible: true,
    is_accessible: !is_interest.value,
    color: '!bg-[#408F93]/5',
  },
  {
    key: 'preferred_return',
    label: 'Preferred Return',
    visible: true,
    is_accessible: !is_interest.value,
    color: '!bg-[#408F93]/5',
  },
  {
    key: 'profit',
    label: 'Gross Profit',
    visible: true,
    is_accessible: !is_interest.value,
    color: '!bg-[#408F93]/5',
  },
  { key: 'principal', label: 'Principal', visible: true, is_accessible: is_interest.value, color: '!bg-[#408F93]/5' },
  { key: 'interest', label: 'Interest', visible: true, is_accessible: is_interest.value, color: '!bg-[#408F93]/5' },
  { key: 'carried_interest_distributed', label: 'Carried Interest', visible: true },
  { key: 'other_fee_distributed', label: 'Distributed Fees', visible: true },
  {
    key: 'net_distributed',
    label: 'Net Distributed',
    disabled: true,
    visible: true,
    color: '!bg-sky-100/50',
  },
  { key: 'hurdle_remaining', label: 'Hurdle Remaining', disabled: true, visible: true },
])

const visibleColumns = computed(() =>
  columns.value.filter((column) => column.visible && column.is_accessible !== false),
)

const collapsed = ref(false)
const toggleTotalCapitalBreakdown = () => {
  collapsed.value = !collapsed.value
  ;['capital', 'preferred_return', 'profit', 'principal', 'interest'].forEach((key) => {
    const column = columns.value.find((column) => column.key === key)
    column.visible = !collapsed.value
  })
}
</script>

<template>
  <div class="relative rounded-lg border border-gray-200">
    <table class="rounded-lg">
      <thead v-if="!collapsed">
        <tr>
          <th :colspan="is_disbursement_new ? 2 : 1"></th>
          <th :colspan="is_interest ? 3 : 4" class="bg-[#408F93]/20">
            <div class="flex items-center justify-center space-x-2">
              <div>Capital Breakdown</div>
              <VButton @click="toggleTotalCapitalBreakdown" size="xs-narrow"
                ><VIcon name="ChevronCollapseHorizontal" class="block h-3 w-3"
              /></VButton>
            </div>
          </th>
          <th colspan="4"></th>
        </tr>
      </thead>
      <thead class="stick">
        <tr>
          <th class="bg-white/50" v-if="is_disbursement_new">
            <input
              class="inverse relative block h-4 w-4 rounded border-gray-300 text-white shadow-sm focus:ring-sky-300 focus:ring-opacity-50"
              type="checkbox"
              @change="toggleAllInvestor($event.target['checked'])"
              checked
              :disabled="!is_disbursement_new"
            />
          </th>
          <th class="bg-white/50 text-left">Investor</th>
          <th v-for="column in visibleColumns" class="bg-white/50 text-right" :class="{ [column.color]: true }">
            <div class="flex items-center justify-end space-x-2">
              <div class="inline-block">{{ column.label }}</div>
              <VButton
                v-if="column.key === 'total_capital' && collapsed"
                @click="toggleTotalCapitalBreakdown"
                size="xs-narrow"
                ><VIcon name="ChevronExpandHorizontal" class="block h-3 w-3"
              /></VButton>
            </div>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr :class="{ disabledRow: !transfer.is_active }" v-for="(transfer, i) in disbursement_transfers">
          <td class="px-3" :class="!transfer.is_active ? 'bg-gray-50' : ''" v-if="is_disbursement_new">
            <input
              @change="toggleInvestor(transfer, $event.target['checked'])"
              type="checkbox"
              :checked="transfer.is_active"
              class="inverse relative block h-4 w-4 rounded border border-gray-300 text-white shadow-sm focus:ring-sky-300 focus:ring-opacity-50"
              :disabled="!is_disbursement_new"
            />
          </td>
          <td class="border-r-[1px] px-2 py-2" :class="!transfer.is_active ? 'bg-gray-50' : ''">
            <div class="">{{ transfer.name }}</div>
            <div class="flex gap-2 text-xs">
              <div class="">Commitment:</div>
              <div class="">{{ n(transfer.commitment, 'currency') }}</div>
            </div>
          </td>
          <td v-for="column in visibleColumns" :class="{ [column.color]: true }">
            <VTextField
              v-if="!['carried_interest_distributed', 'interest_carried_interest'].includes(column.key)"
              v-model="transfer[column.key]"
              :disabled="!transfer.is_active || !!column.disabled"
              :inputClass="`!border-none !shadow-none !mt-0 text-sm !rounded-none text-right ${
                transfer.is_active ? 'bg-transparent' : ''
              }`"
              type="currency"
              :onInput="(e) => onChangeTransfer(transfer, column.key)"
            />
            <div v-else class="relative" :class="transfer.is_active ? 'bg-transparent' : ''">
              <div class="absolute left-2 top-2 rounded bg-sky-100 p-1 py-0.5 text-xs text-gray-500">
                {{ n(getCarriedInterest(transfer), 'percent') }}
              </div>
              <VTextField
                v-model="transfer[column.key]"
                :onChange="(e) => onChangeTransfer(transfer, column.key)"
                inputClass="!border-none !shadow-none !mt-0 text-sm !rounded-none text-right"
                :disabled="!transfer.is_active"
                type="currency"
              />
            </div>
          </td>
        </tr>
      </tbody>
      <tfoot>
        <tr>
          <td v-if="is_disbursement_new"></td>
          <td></td>
          <td v-for="column in visibleColumns" :class="column.color">
            <VTextField
              v-model="aggregateValues[column.key]"
              type="currency"
              :disabled="true"
              inputClass="!border-none !shadow-none !mt-0 text-sm !rounded-none text-right text-gray-800 bg-transparent"
            />
          </td>
        </tr>
      </tfoot>
    </table>
  </div>
</template>

<style scoped>
table {
  @apply w-full border-separate border-spacing-0 overflow-x-auto;
}

thead {
  @apply text-[12px] text-xs uppercase text-gray-700;
}

thead.stick th {
  @apply sticky top-[71px] z-50;
}

th {
  @apply min-w-[100px] whitespace-nowrap border-b border-r border-gray-200 px-3 py-3 backdrop-blur sm:min-w-[10px];
}

thead:first-child th:first-child {
  @apply rounded-tl-lg;
}

th:last-child {
  @apply rounded-tr-lg;
}

@media (min-width: 640px) {
  th.fixed-column {
    @apply first:sticky first:left-0 first:z-10 first:border-r;
  }

  td.fixed-column {
    @apply first:sticky first:left-0 first:z-[1] first:min-w-[320px] first:max-w-[480px] first:border-r first:bg-white;
  }

  tfoot td.fixed-column {
    @apply rounded-bl-lg;
  }
}

tbody {
  @apply text-sm text-gray-700;
}

tbody td {
  @apply whitespace-nowrap border-b border-r border-gray-200;
}

th:first-child,
td:first-child {
  @apply border-l-0;
}

th:last-child,
td:last-child {
  @apply border-r-0;
}

tfoot {
  @apply text-sm font-medium uppercase tracking-wide;
}

tfoot td {
  @apply whitespace-nowrap border-r border-gray-200 text-gray-900;
}

table tbody td,
table tfoot td {
  border-top: none !important;
}
</style>
